import {
  createContext,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { styled } from '@mui/material'
import { EmojiPicker } from 'weblite-web-emoji'
import { useShowAudioPlayer } from '@weblite/audio'
import { WithBodyMessage } from '@weblite/ts-action/message'
import { useHistoryCustomBack, usePanelHistoryTop } from 'weblite-router'
import i18next from 'i18next'
import { useConditionalHook } from '@weblite/use-conditional-hook'
import { PageWithAppBar } from '$common/page-with-app-bar'
import { RoomAppBar, RoomAppBarSelection } from '$common/room-app-bar'
import { MessageList } from '$comp/_app/components/chat/components/body/components/messageList/message-list'
import { Footer } from '$comp/_app/components/chat/components/footer/footer'
import { AudioPlayer } from '$common/audio-player'
import { scrollToMessageByIid, useOpenedRoomId } from '$logic/opened-room'
import {
  closeRoom,
  exitRoomFromSelectionMode,
  setRoomToSelectionMode,
} from '$logic/router'
import {
  canSendMessage,
  getMessageByIid,
  openForwardListHandler,
  handleDeleteMessagesModal,
} from '$logic/message'
import { TopBarsContainer } from '$common/top-bars-container'
import { AppBarBackSearch } from '$common/app-bar/app-bar-back-search'
import { useIsDesktopSize } from '$common/react-hooks/layout'
import { showTextSavedInMemorySnackbar } from '$comp/snackbars'
import { sendFieldApis } from '$comp/_app/components/chat/components/footer/components/send-field'
import { openedRoomMessagesStorage } from '$logic/opened-room/opened-room-messages.storage'
import { SearchResultNavigator } from '$comp/_app/components/chat/components/search-result-navigator/search-result-navigator'
import { editorAPI } from '$comp/_app/components/chat/components/footer/components/send-field/components/text-editor'
import { CallBar } from '$comp/_app/components/chat/components/call-bar'
import { useSearchMessage } from '$logic/message/react-hook'
import { useRoomAttribute } from '$logic/room/react-hook'
import { copyToClipboard } from '$func/clipboard'
import { isPhoneOrTablet } from '$func/environmentInfo'
import { PinViewer } from '$comp/_app/components/chat/components/pin'
import { useOpenedRoomPinnedMessagesIids } from '$logic/pin/react-hooks'
import { AUDIO_PLAYER_HEIGHT } from '$common/audio-player/audio-player-constants'
import { SelectBar } from '$comp/_app/components/chat/components/select-bar/select-bar'
import { useAsyncFunction } from '$func/async-function.react-hook'
import {
  CALL_BAR_HEIGHT,
  JOIN_HALL_REQUESTS_BAR_HEIGHT,
  PIN_VIEWER_HEIGHT,
  SCHEDULED_CALL_BAR_HEIGHT,
  SELECT_BAR_HEIGHT,
} from '$comp/_app/components/chat/components/conf'
import { nothing } from '$helper/functions/utils'
import { ChatBG } from '$comp/_app/components/chat/components/chat-bg'
import { emojiI18n } from '$setup/emoji'
import { BG_GRADIANT } from '$setup/style/constants'
import { ROOM_APP_BAR_HEIGHT } from '$common/room-app-bar/conf'
import {
  FullscreenLocation,
  useHandleFullscreenHistory,
} from '$logic/fullscreen/fullscreen-mode'
import { FileDropZone } from './components/body/components/fileDropZone/fileDropZone.presentational'
import {
  useActionsPermissions,
  useFileDropHandlers,
  useFooterHeight,
  useMultiSelect,
  useShouldShowCallBars,
} from './react-hooks'
import { NotParticipatedHallCallBar } from '$comp/_app/components/chat/components/not-participated-hall-call-bar/not-participated-hall-call-bar'
import { JoinHallRequestsBar } from '$comp/_app/components/chat/components/join-hall-requests-bar'
import {
  useJoinHallRequests,
  useSubscription,
} from '$logic/subscription/react-hook'
import { useWorkspaceColorByRoomId } from '$logic/workspace'

const RoomDropZone = styled('div')`
  display: none;
  width: 100%;
  height: 100%;
  z-index: 2;

  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`

export const SelectionContext = createContext({})
export const SearchContext = createContext(null)

export const roomSearchApi = {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  handleSearch(_newQuery: string) {
    // Empty function
  },
  exitSearch: nothing,
}

// TODO: Refactor hooks -> extract some custom hooks to decrease file size
export function Room() {
  const roomBodyRef = useRef<HTMLDivElement>(null)
  const dropZoneRef = useRef<HTMLDivElement>(null)
  const panel = usePanelHistoryTop()?.state
  const isDesktopSize = useIsDesktopSize()

  useHandleFullscreenHistory(FullscreenLocation.ROOM)

  const roomId = useOpenedRoomId()
  const roomType = useRoomAttribute('type', roomId)
  const isGroup = roomType === 'GROUP'
  const topBarsContainerRef = useRef<HTMLDivElement>()

  const [isJoinHallRequestsBarClosed, setIsJoinHallRequestsBarClosed] =
    useState(false)

  const closeJoinHallRequestsBar = useCallback(() => {
    setIsJoinHallRequestsBarClosed(true)
  }, [])

  const { entity: subscription } =
    useConditionalHook(roomType === 'CHANNEL' || roomType === 'GROUP', {
      hook: useSubscription,
      hookArgs: roomId,
      defaultValue: undefined,
    }) || {}

  const isRestricted = Boolean(subscription?.suspended)

  const {
    selectionMap,
    setSelection,
    deleteSelection,
    resetSelection,
    hasSelection,
    selectedItems,
  } = useMultiSelect()

  const {
    canEdit: canEditSelectedMessages,
    canCopy: canCopySelectedMessages,
    canDelete: canDeleteSelectedMessages,
    canDeleteWapps: canDeleteWappsWithinSelectedMessages,
    canForward: canForwardSelectedMessages,
    arePermissionsStale,
    protectContent,
  } = useActionsPermissions(roomId, selectedItems)
  const { value: canReply, loading: canReplyLoading } = useAsyncFunction(
    () => canSendMessage(roomId),
    [roomId],
  )
  const openSelectBar =
    !canReplyLoading && (canReply || canForwardSelectedMessages) && hasSelection

  const { showAudioPlayer } = useShowAudioPlayer()
  const pinMessages = useOpenedRoomPinnedMessagesIids()
  const showPinBar = useDeferredValue(pinMessages.length > 0)

  const toggleSelection = useCallback(
    (selected: boolean) => (selected ? deleteSelection : setSelection),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const { onDragOver, onDragLeave, onDrop } = useFileDropHandlers({
    roomId,
    dropZoneRef,
    roomType,
  })

  const FOOTER_HEIGHT = useFooterHeight() as number
  const [searchQuery, setSearchQuery] = useState('')
  const [searchMode, setSearchMode] = useState(false)

  useHistoryCustomBack(() => {
    if (hasSelection) resetSelection()
    else if (searchMode) {
      setSearchMode(false)
      setSearchQuery('')
    } else closeRoom()
  }, [searchMode, hasSelection])

  // NOTE: Change panel mode when user select or deselect items
  useEffect(() => {
    if (panel?.mode === 'SELECT' && !selectedItems.length)
      exitRoomFromSelectionMode()

    if (panel?.mode !== 'SELECT' && selectedItems.length)
      if (roomId) setRoomToSelectionMode(roomId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems, roomId])

  // NOTE: Deselect items when user clicked back button
  useEffect(() => {
    if (panel?.mode !== 'SELECT' && selectedItems.length) resetSelection()
    if (panel?.roomId && !isPhoneOrTablet) editorAPI.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panel])

  const handleForward = useCallback(
    async (source: string) => {
      await exitRoomFromSelectionMode()
      await openForwardListHandler(roomId, selectedItems, source)
    },
    [selectedItems, roomId],
  )

  const handleEdit = useCallback(async () => {
    await exitRoomFromSelectionMode()
    const [iidToEdit] = selectedItems
    const message = await openedRoomMessagesStorage.get(iidToEdit)

    if (message) sendFieldApis.setEditContent(message as WithBodyMessage)
  }, [selectedItems])

  const handleCopy = useCallback(async () => {
    await exitRoomFromSelectionMode()
    const messages = (await Promise.all(
      selectedItems.map(openedRoomMessagesStorage.get),
    )) as WithBodyMessage[]

    const messageBodies = messages?.map(({ body }) => body).filter(Boolean)
    copyToClipboard(messageBodies.join('\n\n'))
    showTextSavedInMemorySnackbar()
  }, [selectedItems])

  useEffect(() => {
    setIsJoinHallRequestsBarClosed(false)
  }, [roomId])

  const handleDelete = useCallback(async () => {
    await exitRoomFromSelectionMode()
    if (!roomId) return
    const messages = await Promise.all(
      selectedItems.map(item => getMessageByIid(roomId, item)),
    )

    handleDeleteMessagesModal(
      messages,
      canDeleteWappsWithinSelectedMessages,
      roomId,
    )
  }, [selectedItems, roomId, canDeleteWappsWithinSelectedMessages])

  const handleSearch = useCallback((newQuery: string) => {
    setSearchMode(true)
    setSearchQuery(newQuery)
  }, [])

  const exitSearch = useCallback(() => {
    setSearchMode(false)
    setSearchQuery('')
  }, [])

  useEffect(() => {
    roomSearchApi.handleSearch = handleSearch
    roomSearchApi.exitSearch = exitSearch
  }, [exitSearch, handleSearch])

  const {
    items: searchItems,
    loading,
    debouncedSearchTerm,
  } = useSearchMessage(roomId, searchQuery)

  const [currentSearchIndex, setCurrentSearchIndex] = useState(0)
  useEffect(() => {
    if (!(searchItems && searchItems.length && searchItems[currentSearchIndex]))
      return
    scrollToMessageByIid(searchItems[currentSearchIndex].iid)
  }, [currentSearchIndex, searchItems])

  useEffect(() => {
    setCurrentSearchIndex(0)
  }, [searchQuery])

  const onSearchButtonClick = useCallback(() => {
    setCurrentSearchIndex(1)
    setSearchMode(true)
    analite('CHAT_SEARCH_CLICK')
  }, [])

  useEffect(() => {
    setSearchMode(value => {
      if (value) {
        setSearchQuery('')
        return false
      }
      return value
    })
  }, [roomId])

  const { showNotParticipatedHallCallBar, showCallBar } =
    useShouldShowCallBars(roomId)

  const { items: joinHallRequests } = useJoinHallRequests({
    hallId: roomId,
  })

  const topBarsHeight = useMemo(() => {
    let result = 0

    if (isRestricted) return result

    if (!isDesktopSize) {
      if (showCallBar) result += CALL_BAR_HEIGHT
      if (showAudioPlayer) result += AUDIO_PLAYER_HEIGHT
    }

    if (showNotParticipatedHallCallBar) result += SCHEDULED_CALL_BAR_HEIGHT

    if (showPinBar) result += PIN_VIEWER_HEIGHT

    if (joinHallRequests?.length && !isJoinHallRequestsBarClosed)
      result += JOIN_HALL_REQUESTS_BAR_HEIGHT
    return result
  }, [
    isDesktopSize,
    showNotParticipatedHallCallBar,
    showCallBar,
    showAudioPlayer,
    showPinBar,
    joinHallRequests,
    isJoinHallRequestsBarClosed,
    isRestricted,
  ])

  const appBarBackgroundColor = useWorkspaceColorByRoomId(roomId)

  return (
    <PageWithAppBar
      appBar={
        hasSelection ? (
          <RoomAppBarSelection
            selectionCount={selectedItems.length}
            canCopy={canCopySelectedMessages}
            canEdit={canEditSelectedMessages}
            canDelete={canDeleteSelectedMessages}
            canForward={canForwardSelectedMessages}
            arePermissionsStale={arePermissionsStale}
            onForward={() => handleForward('ROOM_APP_BAR_SELECTION')}
            onCopy={handleCopy}
            onDelete={handleDelete}
            onEdit={handleEdit}
            protectContent={protectContent}
            isGroup={isGroup}
            backgroundColor={appBarBackgroundColor}
          />
        ) : searchMode ? (
          <AppBarBackSearch
            onBack={exitSearch}
            setQuery={setSearchQuery}
            loading={loading}
            value={searchQuery}
            height={ROOM_APP_BAR_HEIGHT}
            backgroundColor={appBarBackgroundColor}
          />
        ) : (
          <RoomAppBar
            roomId={roomId}
            roomType={roomType}
            onBack={closeRoom}
            onSearch={onSearchButtonClick}
            backgroundColor={appBarBackgroundColor}
          />
        )
      }
      rootSx={{ position: 'relative' }}
      bodySx={{
        height: '100%',
        background: BG_GRADIANT,
        position: 'relative',
      }}
    >
      <ChatBG />

      <TopBarsContainer
        isStatic
        ref={topBarsContainerRef} // TODO: is this ref needed ?
        hasAnyChild={
          isRestricted
            ? false
            : (!isDesktopSize && (showCallBar || showAudioPlayer)) ||
              showNotParticipatedHallCallBar ||
              showPinBar ||
              (!!joinHallRequests?.length && !isJoinHallRequestsBarClosed)
        }
      >
        {isRestricted ? null : (
          <>
            {showNotParticipatedHallCallBar && <NotParticipatedHallCallBar />}

            {!isDesktopSize && (showCallBar || showAudioPlayer) && (
              <>
                {showCallBar && <CallBar isInRoom />}
                {showAudioPlayer && <AudioPlayer />}
              </>
            )}

            {showPinBar && <PinViewer roomId={roomId} iids={pinMessages} />}

            {!!joinHallRequests?.length && !isJoinHallRequestsBarClosed && (
              <JoinHallRequestsBar
                roomId={roomId}
                onClose={closeJoinHallRequestsBar}
              />
            )}
          </>
        )}
      </TopBarsContainer>

      <div
        ref={roomBodyRef}
        onDragOver={onDragOver}
        style={{
          position: 'relative',
          width: '100%',
          height: `calc(100% - ${
            openSelectBar
              ? SELECT_BAR_HEIGHT + topBarsHeight
              : FOOTER_HEIGHT + topBarsHeight
          }px)`,
          flexShrink: 0,
        }}
      >
        <SearchContext.Provider value={debouncedSearchTerm}>
          <SelectionContext.Provider value={selectionMap}>
            {!isRestricted && <MessageList toggleSelection={toggleSelection} />}
          </SelectionContext.Provider>
        </SearchContext.Provider>

        <FileDropZone />
        <RoomDropZone
          ref={dropZoneRef}
          onDrop={onDrop}
          onDragLeave={onDragLeave}
        />
        <EmojiPicker
          i18n={emojiI18n}
          addEmoji={editorAPI.insertText}
          locale={i18next.language}
        />
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100%',
        }}
      >
        {searchMode && (
          <SearchResultNavigator
            loading={loading || !searchQuery}
            currentIndex={currentSearchIndex}
            resultLength={searchItems?.length}
            onDown={() => setCurrentSearchIndex(currentSearchIndex - 1)}
            onUp={() => setCurrentSearchIndex(currentSearchIndex + 1)}
          />
        )}
        {openSelectBar && (
          <SelectBar
            isGroup={isGroup}
            protectContent={protectContent}
            onForward={() => handleForward('SELECT_BAR')}
            onReply={
              selectedItems.length === 1 && canReply
                ? async () => {
                    await exitRoomFromSelectionMode()
                    sendFieldApis.setReplyContent(selectedItems[0])
                  }
                : null
            }
            arePermissionsStale={arePermissionsStale}
          />
        )}

        <div
          style={{
            display: openSelectBar || searchMode ? 'none' : 'unset',
          }}
        >
          <Footer />
        </div>
      </div>
    </PageWithAppBar>
  )
}
